import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useLocation } from '@reach/router';
import './ViewingProperty.scss';

const ViewingProperty = ({ propertyId: propPropertyId }) => {
    const location = useLocation();

    // Extract propertyId from URL query parameter or use prop
    const propertyId = useMemo(() => {
        if (propPropertyId) {
            return propPropertyId;
        }

        if (typeof window === 'undefined') {
            return null;
        }

        try {
            const params = new URLSearchParams(location.search);
            const pid = params.get('pid'); 

            if (pid && pid.trim() !== '') {
                console.log('Extracted propertyId from URL:', pid);
                return pid;
            }
            return null;
        } catch (err) {
            console.error('Error extracting propertyId from URL:', err);
            return null;
        }
    }, [location.search, propPropertyId]);

    const [data, setData] = useState({
        image: 'https://placehold.co/416x277',
        title: '',
        subtitle: '',
        price: '',
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Fetch property data from Strapi API
    useEffect(() => {
        if (!propertyId) {
            return;
        }

        const fetchPropertyData = async () => {
            setLoading(true);
            setError(null);

            try {
                const apiUrl = process.env.GATSBY_STRAPI_FORM_URL;
                const apiToken = process.env.GATSBY_STRAPI_FORM_TOKEN;
                const siteUrl = process.env.GATSBY_CLOUD_URL;
                
                // Construct API endpoint
                const endpoint = `${apiUrl}/properties/${propertyId}`;

                // Make API request with Bearer token
                const response = await fetch(endpoint, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${apiToken}`,
                    },
                });

                // Handle HTTP errors
                if (!response.ok) {
                    throw new Error(`API request failed with status ${response.status}: ${response.statusText}`);
                }

                // Parse response
                const apiData = await response.json();

                // Extract property data from Strapi response
                // Assumes API returns: { data: { attributes: { ... } } }
                const propertyAttributes = apiData || {};

                // Handle images - check if it's an array and get the first one
                let imageUrl = 'https://placehold.co/416x277';
                if (propertyAttributes.images && Array.isArray(propertyAttributes.images) && propertyAttributes.images.length > 0) {
                    // Try both 'url' and 'srcUrl' properties
                    imageUrl = propertyAttributes.images[0].large_url || propertyAttributes.images[0].url || propertyAttributes.images[0].srcUrl || imageUrl;
                } else if (propertyAttributes.image?.url) {
                    // Fallback to single image property
                    imageUrl = propertyAttributes.image.large_url || propertyAttributes.image.url;
                }
                let propertyLink = `${siteUrl}/property-${propertyAttributes.searchType === "sales" ? "for-sale" : "to-rent"}/${propertyAttributes.slug}-${propertyAttributes.id}/`;
                if (typeof window !== 'undefined') {
                    localStorage.setItem('propertyLink', propertyLink);
                    localStorage.setItem('propertyName', propertyAttributes.intro);
                    localStorage.setItem('propertyType', propertyAttributes.search_type);
                        
                }
                setData({
                    image: imageUrl,
                    title: propertyAttributes.intro || '',
                    subtitle: `${propertyAttributes.title || ''} in ${propertyAttributes.postcode ? propertyAttributes.postcode.split(' ')[0] : ''}`,
                    price: `£ ${propertyAttributes.price ? propertyAttributes.price.toLocaleString() : '0'} ${propertyAttributes.search_type === 'lettings' ? 'PW' : ''} ${propertyAttributes.price_qualifier === 'Offers In Excess' ? propertyAttributes.price_qualifier : ''}`.trim(),
                });
            } catch (err) {
                console.error('Error fetching property data:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchPropertyData();
    }, [propertyId]);

    // Return null if no propertyId provided
    if (!propertyId) {
        return null;
    }

    return (
        <div className="viewing-property" role="group" aria-label={data.title}>
            <div className="divider" />

            {/* Loading state */}
            {loading && (
                <div className="content">
                    <div className="loading-state">Loading property data...</div>
                </div>
            )} 
         

            {/* Content - renders when loaded */}
            {!loading && !error && (
                <div className="content">
                    <img
                        className="property-image"
                        src={data.image}
                        alt={data.title || 'Property image'} 
                        loading="lazy"
                    />
                    <div className="details"> 
                        <div className="text-block">
                            <div className="title">{data.title}</div>
                            <div className="subtitle">{data.subtitle}</div>
                            <div className="price">{data.price}</div>
                        </div> 

                    </div>
                </div>
            )}

            <div className="divider" />
        </div>
    );
};

export default ViewingProperty;
